/**
 * Accessibility Utilities
 * 
 * Componentes y hooks para mejorar la accesibilidad:
 * - Skip links
 * - Screen reader announcements
 * - Focus management
 * - ARIA helpers
 */

import React, { useEffect, useRef, useState } from 'react';

// ============================================================
// SKIP LINK - Saltar al contenido principal
// ============================================================

/**
 * Link para saltar la navegación (accesibilidad con teclado)
 * Debe colocarse al inicio del Layout
 */
export function SkipLink({ targetId = 'main-content', children = 'Saltar al contenido' }) {
  return (
    <a
      href={`#${targetId}`}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 
                 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white 
                 focus:rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
    >
      {children}
    </a>
  );
}

// ============================================================
// SCREEN READER ONLY - Texto solo para lectores de pantalla
// ============================================================

/**
 * Oculta visualmente pero mantiene accesible para screen readers
 */
export function ScreenReaderOnly({ children, as: Component = 'span' }) {
  return (
    <Component className="sr-only">
      {children}
    </Component>
  );
}

// ============================================================
// LIVE REGION - Anuncios para screen readers
// ============================================================

/**
 * Región ARIA live para anuncios dinámicos
 * Uso: <LiveRegion message={announcement} />
 */
export function LiveRegion({ 
  message, 
  politeness = 'polite', // 'polite' | 'assertive'
  clearAfter = 5000 
}) {
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (message) {
      setAnnouncement(message);
      
      if (clearAfter > 0) {
        const timer = setTimeout(() => setAnnouncement(''), clearAfter);
        return () => clearTimeout(timer);
      }
    }
  }, [message, clearAfter]);

  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}

// ============================================================
// FOCUS TRAP - Atrapar foco en modales
// ============================================================

/**
 * Hook para atrapar el foco dentro de un elemento (modales, dialogs)
 */
export function useFocusTrap(isActive = true) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element on mount
    firstElement?.focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  return containerRef;
}

// ============================================================
// FOCUS ON MOUNT - Foco automático al montar
// ============================================================

/**
 * Hook para enfocar un elemento al montar
 */
export function useFocusOnMount() {
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return ref;
}

// ============================================================
// KEYBOARD NAVIGATION - Navegación con teclado
// ============================================================

/**
 * Hook para navegación con flechas en listas
 */
export function useArrowNavigation(items, onSelect) {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect?.(items[activeIndex], activeIndex);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(items.length - 1);
        break;
    }
  };

  return { activeIndex, setActiveIndex, handleKeyDown };
}

// ============================================================
// ACCESSIBLE ICON BUTTON
// ============================================================

/**
 * Botón con icono accesible (requiere aria-label)
 */
export function IconButton({ 
  icon: Icon, 
  label, 
  onClick, 
  className = '',
  disabled = false,
  size = 'md'
}) {
  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`
        ${sizeClasses[size]}
        rounded-md hover:bg-muted 
        focus:outline-none focus:ring-2 focus:ring-blue-500
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      <Icon className={iconSizes[size]} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </button>
  );
}

// ============================================================
// ACCESSIBLE TABLE
// ============================================================

/**
 * Wrapper para tablas accesibles
 */
export function AccessibleTable({ 
  caption, 
  children, 
  className = '' 
}) {
  return (
    <div className="overflow-x-auto" role="region" aria-label={caption}>
      <table className={`min-w-full ${className}`}>
        {caption && (
          <caption className="sr-only">{caption}</caption>
        )}
        {children}
      </table>
    </div>
  );
}

// ============================================================
// FORM FIELD WITH ACCESSIBILITY
// ============================================================

/**
 * Campo de formulario con labels y errores accesibles
 */
export function AccessibleField({
  id,
  label,
  error,
  required = false,
  helpText,
  children
}) {
  const errorId = error ? `${id}-error` : undefined;
  const helpId = helpText ? `${id}-help` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1">
      <label 
        htmlFor={id} 
        className="block text-sm font-medium text-foreground"
      >
        {label}
        {required && (
          <span className="text-red-500 ml-1" aria-hidden="true">*</span>
        )}
        {required && <span className="sr-only">(requerido)</span>}
      </label>
      
      {React.cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? 'true' : undefined,
        'aria-required': required || undefined
      })}
      
      {helpText && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {helpText}
        </p>
      )}
      
      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ============================================================
// VISUALLY HIDDEN (alias for sr-only)
// ============================================================

export function VisuallyHidden({ children }) {
  return <span className="sr-only">{children}</span>;
}

// ============================================================
// ANNOUNCE HOOK - Para anuncios programáticos
// ============================================================

/**
 * Hook para hacer anuncios a screen readers
 */
export function useAnnounce() {
  const [message, setMessage] = useState('');
  const timerRef = useRef(null);

  const announce = (text, politeness = 'polite') => {
    // Clear first to ensure re-announcement of same message
    setMessage('');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMessage(text), 100);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const Announcer = () => (
    <LiveRegion message={message} />
  );

  return { announce, Announcer };
}

// ============================================================
// EXPORTS
// ============================================================

export default {
  SkipLink,
  ScreenReaderOnly,
  LiveRegion,
  useFocusTrap,
  useFocusOnMount,
  useArrowNavigation,
  IconButton,
  AccessibleTable,
  AccessibleField,
  VisuallyHidden,
  useAnnounce
};